import { IUpsellProductVariant } from './types';

// Shopify sends money as strings. Absent or unparseable values collapse to 0 so
// callers can do arithmetic without guarding every access.
export const parsePrice = (raw: string | undefined | null): number => {
  if (!raw) return 0;
  const n = Number(raw);

  return Number.isFinite(n) ? n : 0;
};

/**
 * Whether a variant is genuinely on offer.
 *
 * The two fields are a pair: `originalPrice` is the standard price and `price` is
 * the reduced one. A markdown therefore needs BOTH halves to be usable. A variant
 * carrying an `originalPrice` with no `price` is incomplete feed data, not a
 * discount — treating it as one produced a `-20%` badge beside a struck-through
 * standard price and a live price of `0,00 €`.
 *
 * Compares numbers rather than testing truthiness, which is what the old
 * `!!variant.originalPrice` check got wrong twice over:
 *   - `originalPrice: "0"` is a truthy STRING, so it counted as a discount; and
 *   - an empty `price` was never looked at at all.
 *
 * `originalPrice > price` is the last condition, and it is what makes the two
 * field names true. Equal values are not a discount, so a feed that ships the
 * same number in both fields renders as a plain price rather than as a 0% saving.
 */
export const isVariantDiscounted = (variant: IUpsellProductVariant | undefined): boolean => {
  const price = parsePrice(variant?.price);
  const originalPrice = parsePrice(variant?.originalPrice);

  return price > 0 && originalPrice > price;
};
